import { useState } from 'react';
import SheetDate from './SheetDate';
import dayjs from 'dayjs';

// Period selector for the Earnings page. Emits { from, to } as inclusive
// 'YYYY-MM-DD' strings (or null/null for "All time") — the exact shape the
// earnings endpoints accept.
//
// Mobile-first: presets are a horizontally-scrollable chip row (no label
// truncation), and "Custom" is two single antd DatePickers — the same calendar
// used across the app's forms. (Never the dual-panel RangePicker: its two-month
// popup overflows phone screens.) inputReadOnly keeps the keyboard down.

const fmt = (d) => (d ? d.format('YYYY-MM-DD') : null);

export const ALL_TIME = { from: null, to: null };

// Named presets resolved against "today" at click time.
export const presetRange = (key) => {
  const now = dayjs();
  switch (key) {
    case 'This month': return { from: fmt(now.startOf('month')), to: fmt(now.endOf('month')) };
    case 'Last month': {
      const m = now.subtract(1, 'month');
      return { from: fmt(m.startOf('month')), to: fmt(m.endOf('month')) };
    }
    case 'This year': return { from: fmt(now.startOf('year')), to: fmt(now.endOf('year')) };
    default: return ALL_TIME;
  }
};

const PRESETS = ['All', 'This month', 'Last month', 'This year', 'Custom'];

export default function PeriodPicker({ value, onChange }) {
  const [preset, setPreset] = useState('This year');

  const pick = (key) => {
    setPreset(key);
    if (key === 'Custom') {
      // Prefill with the current selection (or this month) so the agent adjusts
      // two filled fields instead of starting from blanks.
      if (!(value?.from && value?.to)) onChange(presetRange('This month'));
      return;
    }
    onChange(key === 'All' ? ALL_TIME : presetRange(key));
  };

  // Emit whenever an end changes; swap if the agent picks them backwards.
  const setEnd = (which, d) => {
    const next = { from: value?.from || null, to: value?.to || null, [which]: fmt(d) };
    if (next.from && next.to && next.from > next.to) [next.from, next.to] = [next.to, next.from];
    onChange(next.from || next.to ? next : ALL_TIME);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="scrx" style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 2 }}>
        {PRESETS.map((k) => {
          const active = preset === k;
          return (
            <div
              key={k}
              onClick={() => pick(k)}
              style={{ flex: 'none', fontSize: 12.5, fontWeight: 600, padding: '6px 13px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap', background: active ? 'var(--primary)' : 'var(--surface2)', color: active ? '#fff' : 'var(--muted)', border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}` }}
            >
              {k}
            </div>
          );
        })}
      </div>
      {preset === 'Custom' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SheetDate
            title="Period from"
            value={value?.from ? dayjs(value.from) : null}
            onChange={(d) => setEnd('from', d)}
            placeholder="From"
            format="DD MMM YY"
            style={{ flex: 1, minWidth: 0 }}
          />
          <span style={{ color: 'var(--muted)', fontSize: 12.5, flex: 'none' }}>→</span>
          <SheetDate
            title="Period to"
            value={value?.to ? dayjs(value.to) : null}
            onChange={(d) => setEnd('to', d)}
            placeholder="To"
            format="DD MMM YY"
            style={{ flex: 1, minWidth: 0 }}
          />
        </div>
      )}
    </div>
  );
}
